/**
 * 🛡️ G2GENTE - Tela de Cadastro Simplificado (PWA)
 * Mobile-first - Coleta apenas dados básicos para cadastro rápido
 * Campos curriculares (cidade, estado, emprego) são preenchidos posteriormente
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { candidatesAPI, partnerPortalAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { PartnerOriginSelect } from '../../components/candidates/PartnerOriginSelect';
import { 
  Loader2, 
  ArrowLeft, 
  Mail, 
  Lock, 
  User, 
  Phone,
  Eye,
  EyeOff,
  Shield,
  CheckCircle,
  AlertCircle,
  Smartphone,
  Download
} from 'lucide-react';

// Máscara de telefone brasileiro
const formatPhone = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) return `(${numbers}`;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

// Máscara de CPF
const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

// Validação de CPF
const validateCPF = (cpf) => {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1+$/.test(digits)) return false;
  
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let d1 = (sum * 10 % 11) % 10;
  if (d1 !== parseInt(digits[9])) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  let d2 = (sum * 10 % 11) % 10;
  if (d2 !== parseInt(digits[10])) return false;
  
  return true;
};

// Validação de telefone
const isValidPhone = (phone) => {
  const numbers = phone.replace(/\D/g, '');
  return numbers.length >= 10 && numbers.length <= 11;
};

// Validação de email
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Componente de erro de input (definido fora do componente principal)
const InputError = ({ message }) => (
  message ? (
    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
      <AlertCircle className="w-3 h-3" />
      {message}
    </p>
  ) : null
);

export default function MobileRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Se veio de um convite de teste, redirecionar após cadastro
  const redirectTo = searchParams.get('redirect');
  const inviteCode = searchParams.get('entidade');
  const [invitePartner, setInvitePartner] = useState(null);
  useEffect(() => {
    if (!inviteCode) return;
    partnerPortalAPI.byInvite(inviteCode).then((r) => {
      setInvitePartner(r.data);
      setForm((f) => ({ ...f, partner_id: r.data.id }));
    }).catch(() => {});
  }, [inviteCode]);
  
  const [form, setForm] = useState({
    nome: '',
    cpf: '',
    email: '',
    telefone: '',
    senha: '',
    confirmarSenha: '',
    aceiteLGPD: false
  });

  const handleChange = (field, value) => {
    // Limpar erro do campo ao editar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Aplicar máscaras
    if (field === 'telefone') {
      value = formatPhone(value);
    } else if (field === 'cpf') {
      value = formatCPF(value);
    }
    
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Nome
    if (!form.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (form.nome.trim().length < 3) {
      newErrors.nome = 'Nome deve ter pelo menos 3 caracteres';
    } else if (!form.nome.trim().includes(' ')) {
      newErrors.nome = 'Informe nome e sobrenome';
    }
    
    // CPF
    if (!form.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (!validateCPF(form.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }
    
    // Email
    if (!form.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!isValidEmail(form.email)) {
      newErrors.email = 'E-mail inválido';
    }
    
    // Telefone
    if (!form.telefone.trim()) {
      newErrors.telefone = 'Celular é obrigatório';
    } else if (!isValidPhone(form.telefone)) {
      newErrors.telefone = 'Celular inválido. Use (DDD) 9XXXX-XXXX';
    }
    
    // Senha
    if (!form.senha) {
      newErrors.senha = 'Senha é obrigatória';
    } else if (form.senha.length < 8) {
      newErrors.senha = 'Senha deve ter pelo menos 8 caracteres';
    }
    
    // Confirmar Senha
    if (!form.confirmarSenha) {
      newErrors.confirmarSenha = 'Confirmação de senha é obrigatória';
    } else if (form.senha !== form.confirmarSenha) {
      newErrors.confirmarSenha = 'As senhas não coincidem';
    }
    
    // LGPD
    if (!form.aceiteLGPD) {
      newErrors.aceiteLGPD = 'Você deve aceitar os termos para continuar';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setLoading(true);
    try {
      // 1. Criar conta de usuário com CPF
      await register(form.cpf, form.email, form.senha, 'CANDIDATE');
      
      // 2. Criar perfil de candidato com dados básicos apenas
      await candidatesAPI.create({
        nome: form.nome.trim(),
        telefone: form.telefone,
        cidade: '',
        estado: '',
        localizacao: '',
        cargo: '',
        experiencia: '',
        consent_share: false,
        esta_trabalhando: null,
        cnpj_empresa_atual: null,
        empresa_atual: null,
        partner_id: form.partner_id || null
      });
      
      toast.success('Conta criada com sucesso!');
      
      // Redirecionar para onde o usuário veio (ex: convite de teste) ou para home
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/m');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao cadastrar. Tente novamente.';
      if (message.includes('CPF já cadastrado')) {
        setErrors({ cpf: 'Este CPF já está cadastrado' });
        toast.error('Este CPF já está cadastrado');
      } else if (message.includes('Email já cadastrado')) {
        setErrors({ email: 'Este e-mail já está cadastrado' });
        toast.error('Este e-mail já está cadastrado');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800" data-testid="mobile-register">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-sm p-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/m/login')} className="text-slate-400">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-white">Criar Conta</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <User className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">Comece sua jornada</h2>
          <p className="text-slate-400 text-sm">Preencha seus dados básicos para criar sua conta</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
          
          {/* Nome Completo */}
          <div className="space-y-1">
            <Label className="text-slate-300 text-sm">Nome Completo *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type="text"
                placeholder="Seu nome completo"
                value={form.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
                className={`pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${
                  errors.nome ? 'border-red-500' : ''
                }`}
                data-testid="register-nome"
              />
            </div>
            <InputError message={errors.nome} />
          </div>

          {/* CPF */}
          <div className="space-y-1">
            <Label className="text-slate-300 text-sm">CPF *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type="text"
                placeholder="000.000.000-00"
                value={form.cpf}
                onChange={(e) => handleChange('cpf', e.target.value)}
                maxLength={14}
                className={`pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${
                  errors.cpf ? 'border-red-500' : ''
                }`}
                data-testid="register-cpf"
              />
            </div>
            <InputError message={errors.cpf} />
            {form.cpf && validateCPF(form.cpf) && (
              <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                CPF válido
              </p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1">
            <Label className="text-slate-300 text-sm">E-mail *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type="email"
                placeholder="seu@email.com"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value.toLowerCase())}
                className={`pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${
                  errors.email ? 'border-red-500' : ''
                }`}
                data-testid="register-email"
              />
            </div>
            <InputError message={errors.email} />
          </div>

          {/* Celular */}
          <div className="space-y-1">
            <Label className="text-slate-300 text-sm">Celular *</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type="tel"
                placeholder="(11) 99999-9999"
                value={form.telefone}
                onChange={(e) => handleChange('telefone', e.target.value)}
                className={`pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${
                  errors.telefone ? 'border-red-500' : ''
                }`}
                maxLength={16}
                data-testid="register-telefone"
              />
            </div>
            <InputError message={errors.telefone} />
          </div>

          {invitePartner ? (
            <div className="rounded-lg bg-amber-500/15 border border-amber-500/40 p-3 text-sm text-amber-200" data-testid="register-invite-partner">
              Cadastro pelo convite de <strong>{invitePartner.nome}</strong> ({invitePartner.municipio}/{invitePartner.estado})
            </div>
          ) : (
            <PartnerOriginSelect dark value={form.partner_id} onChange={(v) => setForm((f) => ({ ...f, partner_id: v }))} />
          )}

          {/* Senha */}
          <div className="space-y-1">
            <Label className="text-slate-300 text-sm">Senha *</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Mínimo 8 caracteres"
                value={form.senha}
                onChange={(e) => handleChange('senha', e.target.value)}
                className={`pl-10 pr-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${
                  errors.senha ? 'border-red-500' : ''
                }`}
                data-testid="register-senha"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <InputError message={errors.senha} />
            {form.senha && form.senha.length >= 8 && !errors.senha && (
              <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Senha válida
              </p>
            )}
          </div>

          {/* Confirmar Senha */}
          <div className="space-y-1">
            <Label className="text-slate-300 text-sm">Confirmar Senha *</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Repita a senha"
                value={form.confirmarSenha}
                onChange={(e) => handleChange('confirmarSenha', e.target.value)}
                className={`pl-10 pr-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${
                  errors.confirmarSenha ? 'border-red-500' : ''
                }`}
                data-testid="register-confirmar-senha"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <InputError message={errors.confirmarSenha} />
            {form.confirmarSenha && form.senha === form.confirmarSenha && !errors.confirmarSenha && (
              <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Senhas coincidem
              </p>
            )}
          </div>

          {/* Aceite LGPD */}
          <div className="pt-2">
            <div 
              className={`rounded-lg p-4 ${
                errors.aceiteLGPD 
                  ? 'bg-red-900/20 border border-red-500/50' 
                  : 'bg-slate-800/50 border border-slate-700'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="lgpd"
                  checked={form.aceiteLGPD}
                  onCheckedChange={(checked) => handleChange('aceiteLGPD', checked)}
                  className="mt-1 border-slate-500 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
                  data-testid="register-lgpd"
                />
                <div className="flex-1">
                  <Label htmlFor="lgpd" className="text-slate-300 text-sm cursor-pointer">
                    <Shield className="w-4 h-4 inline mr-1 text-cyan-500" />
                    Li e aceito os{' '}
                    <a href="/termos" target="_blank" className="text-cyan-400 underline">
                      Termos de Uso
                    </a>{' '}
                    e a{' '}
                    <a href="/privacidade" target="_blank" className="text-cyan-400 underline">
                      Política de Privacidade
                    </a>{' '}
                    conforme a LGPD.
                  </Label>
                  <p className="text-slate-400 text-xs mt-1">
                    Ao me cadastrar, autorizo que empresas cadastradas na G2eGente vejam meu nome, telefone, e-mail e currículo para me chamar para vagas. Você será avisado quando uma empresa visualizar seu perfil.
                  </p>
                </div>
              </div>
              <InputError message={errors.aceiteLGPD} />
            </div>
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full bg-cyan-600 hover:bg-cyan-700 h-12 text-lg font-semibold mt-4"
            disabled={loading}
            data-testid="register-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Criando conta...
              </>
            ) : (
              'Criar Conta'
            )}
          </Button>

          {/* Login Link */}
          <p className="text-center text-slate-400 pt-4">
            Já tem conta?{' '}
            <Link to="/m/login" className="text-cyan-400 font-medium hover:underline">
              Entrar
            </Link>
          </p>

          {/* Download App Banner */}
          <div className="mt-6 p-4 bg-gradient-to-r from-cyan-600/20 to-purple-600/20 rounded-xl border border-cyan-500/30">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-sm">Instale o App G2eGente</h3>
                <p className="text-slate-400 text-xs">Acesse vagas e notificações no celular</p>
              </div>
              <Button 
                type="button"
                size="sm"
                onClick={() => {
                  if (window.deferredPrompt) {
                    window.deferredPrompt.prompt();
                  } else {
                    toast.info('Acesse pelo navegador do celular e clique em "Adicionar à tela inicial"');
                  }
                }}
                className="bg-cyan-500 hover:bg-cyan-600 text-white text-xs"
              >
                <Download className="w-3 h-3 mr-1" />
                Instalar
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
